import {createContext, useCallback, useContext, useEffect, useReducer, useState} from 'react'
import type { FC, ReactNode } from 'react'
import { setCookie, parseCookies } from 'nookies'
import { useLocalStorage } from '.'
import {useRouter} from "next/router";

interface Customer {
  [key: string]: any
}

interface Coupon {
  [key: string]: any
}

interface Item {
  id: string
  isAtacado: boolean
  price: number
  quantity?: number
  period?: string
  itemTotal?: number
  type: 'convencional' | 'experiencia'
  delivery_method?: string,
  [key: string]: any,
}

type ShippingType = 'nacional' | 'local'

interface InitialState {
  id: string
  items: Item[]
  itemsToOrder: Item[]
  cartContext: 'conventional' | 'subscription' | 'experience' | null
  isEmpty: boolean
  totalItems: number
  totalUniqueItems: number
  totalShipping: number
  cartTotal: number
  metadata?: Metadata
  customer: Customer
  coupon: Coupon
  shipping: Shipping[]
  block: boolean
  zipCode: string
  shippingType: ShippingType
  note: string
  popup: boolean
  selectedSellersDeliveryMethod: string[];
}

interface Metadata {
  [key: string]: any
}

interface Shipping {
  description: string
  value: number
  sellers: any
  sellersId: any[]
  deliveryKey?: string | string[]
}

interface CartProviderState extends InitialState {
  addItem: (item: Item, quantity?: number) => void
  removeItem: (id: Item['id']) => void
  removeExpItem: (id: number) => void
  updateItem: (id: Item['id'], payload: object) => void
  updateItemQuantity: (id: Item['id'], quantity: number) => void
  updatePeriod: (id: Item['id'], period: string) => void
  emptyCart: () => void
  getItem: (id: Item['id']) => any | undefined
  inCart: (id: Item['id']) => boolean
  updateCartMetadata: (metadata: Metadata) => void
  setCustomer: (customer: Customer) => void
  setCoupon: (coupon: Coupon) => void
  setShipping: (shipping: Shipping[]) => void
  setBlock: (block: boolean | string) => void
  setZipCode: (zipcode: string) => void
  setShippingType: (shippingType: ShippingType) => void
  setNote: (note: string) => void
  setPopup: (popup: boolean) => void
  setDeliveryMethod: (itemID: Item['id'], deliveryMethod: string) => void
  setSelectedSellersDeliveryMethod: (value: string[] | ((prev: string[]) => string[])) => void
  updateSellerMethodDeliveryKey: (newEntry: string) => void
  updateCartContext: (context: 'conventional' | 'subscription' | 'experience' | null) => void
  autoUpdateCartContext: () => void
}

const calculateItemTotals = (items: Item[]) =>
  items.map((item) => ({
    ...item,
    itemTotal: item.price * item.quantity!,
  }))

const calculateTotal = (
  items: Item[],
  context: 'subscription' | 'experience' | 'conventional' | null = null
) => {
  return items.reduce((total, item) => {
    // Contexto nulo: calcula TODOS os itens
    if (context === null) {
      // Itens de assinatura (aplica desconto)
      if (item.assinatura) {
        const price = item.assinatura_desconto_produtor > 0
          ? item.price * (1 - item.assinatura_desconto_produtor / 100)
          : item.price;
        return total + item.quantity! * price;
      }

      // Demais itens (preço normal)
      return total + item.quantity! * item.price;
    }

    // Contexto de assinatura
    if (context === 'subscription' && item.assinatura) {
      if (item.period == '') {
        item.period = 'semanal';
      }
      const price = item.assinatura_desconto_produtor > 0
        ? item.price * (1 - item.assinatura_desconto_produtor / 100)
        : item.price;
      return total + item.quantity! * price;
    }

    // Contexto de experiência
    if (context === 'experience' && (item.type === 'experiencia' || item.experiencia_id)) {
      return total + item.quantity! * item.price;
    }

    // Contexto convencional
    if (context === 'conventional' && !item.assinatura && !item.experiencia_id) {
      return total + item.quantity! * item.price;
    }

    return total;
  }, 0);
};

const calculateTotalItems = (items: Item[]) => {
  return items.reduce((sum, item) => sum + item.quantity!, 0)
}

const calculateTotalShipping = (shipping: Shipping[], selectedSellersDeliveryMethod?: any) => {
  let total = 0

  let filteredShipping: any[] = [];

  if (shipping && Array.isArray(shipping) && Array.isArray(selectedSellersDeliveryMethod)) {
    filteredShipping = shipping.filter((item) => {
      if (selectedSellersDeliveryMethod.includes(item.deliveryKey)) {
        return true;
      }

      if (Array.isArray(item.deliveryKey)) {
        return selectedSellersDeliveryMethod.some((key) => {
          const [sellerSlug, deliveryMethod] = key.split("/");
          if (deliveryMethod === "fazendinhalog" && item.deliveryKey) {
            return (item.deliveryKey as string[]).includes(sellerSlug);
          }
          return false;
        });
      }

      // Formato FazendinhaLog com múltiplos slugs: "slug1,slug2/fazendinhalog"
      if (typeof item.deliveryKey === 'string') {
        const lastSlash = item.deliveryKey.lastIndexOf('/');
        const slugsPart = item.deliveryKey.substring(0, lastSlash);
        const method = item.deliveryKey.substring(lastSlash + 1);
        const slugs = slugsPart.split(',');
        if (slugs.length > 1) {
          return selectedSellersDeliveryMethod.some((key: string) => {
            const [sellerSlug, deliveryMethod] = key.split('/');
            return deliveryMethod === method && slugs.includes(sellerSlug);
          });
        }
      }

      return false;
    });
  }

  if (filteredShipping && filteredShipping.length > 0) {
    // eslint-disable-next-line no-return-assign
    filteredShipping.map((item) => (total += item.value))
  }
  return total
}

const calculateUniqueItems = (items: Item[]) => {
  return items.length
}

export type Actions =
  | { type: 'SET_ITEMS'; payload: Item[] }
  | { type: 'ADD_ITEM'; payload: Item }
  | { type: 'REMOVE_ITEM'; id: Item['id'] }
  | {
      type: 'UPDATE_ITEM'
      id: Item['id']
      payload: object
    }
  | { type: 'EMPTY_CART' }
  | { type: 'UPDATE_CART_META'; payload: Metadata }
  | { type: 'SET_CUSTOMER'; payload: Customer }
  | { type: 'SET_COUPON'; payload: Coupon }
  | { type: 'SET_SHIPPING'; payload: Shipping }
  | { type: 'SET_BLOCK'; payload: boolean | string }
  | { type: 'SET_ZIPCODE'; payload: string }
  | { type: 'SET_SHIPPING_TYPE'; payload: ShippingType }
  | { type: 'SET_NOTE'; payload: string }
  | { type: 'REMOVE_EXP_ITEM'; id: number }
  | { type: 'SET_POPUP'; payload: boolean }
  | { type: 'SET_SELECTED_SELLERS_DELIVERY_METHOD'; payload: string[] }
  | { type: 'UPDATE_SELECTED_SELLERS_DELIVERY_METHOD'; payload: string }
  | { type: 'UPDATE_CART_CONTEXT'; payload: 'conventional' | 'subscription' | 'experience' | null }

export const initialState: any = {
  items: [],
  itemsToOrder: [],
  cartContext: null,
  isEmpty: true,
  totalItems: 0,
  totalUniqueItems: 0,
  totalShipping: 0,
  cartTotal: 0,
  metadata: {},
  customer: {},
  coupon: {},
  shipping: [],
  block: false,
  zipCode: null as unknown as string,
  shippingType: 'nacional',
  popup: false,
  selectedSellersDeliveryMethod: [],
}

const CartContext = createContext<CartProviderState | undefined>(initialState)

export const createCartIdentifier = (len = 12) =>
  // eslint-disable-next-line no-bitwise
  [...Array(len)].map(() => (~~(Math.random() * 36)).toString(36)).join('')

export const useCart = () => {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('Expected to be wrapped in a CartProvider')
  }

  return context
}

const filterItemsByContext = (items: Item[], context: string | null) => {
  if (!context) return [...items];

  if (context === 'experience') {
    return items.filter(item => item.experiencia_id != null);
  } else if (context === 'subscription') {
    return items.filter(item => item.assinatura === true);
  } else if (context === 'conventional') {
    return items.filter(item => !(item.experiencia_id) && !(item.assinatura))
  } else {
    return items
  }
};

const generateCartState = (state = initialState, items: Item[]) => {
  const fullItems = [...items];

  const itemsToOrder = state.cartContext
    ? filterItemsByContext(fullItems, state.cartContext)
    : [...fullItems];

  const totalUniqueItems = calculateUniqueItems(itemsToOrder);
  const isEmpty = totalUniqueItems === 0;

  if(state.context === 'experience') state.shipping = [];

  return {
    ...initialState,
    ...state,
    items: calculateItemTotals(fullItems),
    itemsToOrder,
    totalItems: calculateTotalItems(itemsToOrder),
    totalUniqueItems,
    cartTotal: calculateTotal(itemsToOrder, state.cartContext),
    totalShipping: calculateTotalShipping(state.shipping, state.selectedSellersDeliveryMethod),
    isEmpty,
  };
};

function reducer(state: CartProviderState, action: Actions) {
  switch (action.type) {
    case 'SET_ITEMS':
      return generateCartState(state, action.payload)

    case 'SET_POPUP':
      return {
        ...state,
        popup: action.payload,
      }

    case 'ADD_ITEM': {
      const items = [...state.items, action.payload]

      return generateCartState(state, items)
    }

    case 'UPDATE_ITEM': {
      const items = state.items.map((item: Item) => {
        if (
          item.type === 'experiencia' &&
          item.experiencia_id &&
          // @ts-ignore
          item.experiencia_id.toString() !== action.id
        ) {
          return item
        }
        // @ts-ignore
        if (item.type === 'convencional' && parseInt(item.id) != action.id) {
          return item
        }

        return {
          ...item,
          ...action.payload,
        }
      })

      return generateCartState(state, items)
    }

    case 'REMOVE_ITEM': {
      const isConventional = state.items.find((i) => i.id.toString() === action.id.toString())
      if (isConventional && isConventional.type === 'convencional') {
        // @ts-ignore
        const items = state.items.filter((i: Item) => i.id.toString() !== action.id.toString())
        return generateCartState(state, items)
      }

      const items = state.items.filter((i: Item) =>
        i.type === 'experiencia' ? i.experiencia_id.toString() !== action.id.toString() : true,
      )

      return generateCartState(state, items)
    }

    case 'REMOVE_EXP_ITEM': {
      const items = state.items.filter((i: Item) => i.id.toString() !== action.id.toString())
      return generateCartState(state, items)
    }

    case 'EMPTY_CART':
      return initialState

    case 'UPDATE_CART_META':
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.payload,
        },
      }

    case 'SET_BLOCK':
      return {
        ...state,
        block: action.payload,
      }

    case 'SET_CUSTOMER':
      return {
        ...state,
        ...action.payload,
      }

    case 'SET_COUPON':
      return {
        ...state,
        ...action.payload,
      }

    case 'SET_SHIPPING':
      return {
        ...generateCartState(state, state.items),
        shipping: action.payload,
        totalShipping: calculateTotalShipping(action.payload as unknown as Shipping[], state.selectedSellersDeliveryMethod),
      }

    case 'SET_ZIPCODE':
      return {
        ...state,
        zipCode: action.payload,
      }

    case 'SET_SHIPPING_TYPE':
      return {
        ...state,
        shippingType: action.payload,
      }

    case 'SET_NOTE':
      return {
        ...state,
        note: action.payload,
      }

    case 'SET_SELECTED_SELLERS_DELIVERY_METHOD':
      return {
        ...state,
        selectedSellersDeliveryMethod: action.payload,
      };

    case 'UPDATE_SELECTED_SELLERS_DELIVERY_METHOD': {
      const newEntry = action.payload;
      const sellerSlug = newEntry.split('/')[0];

      const updatedMethods = state.selectedSellersDeliveryMethod.filter(
        (entry) => entry.split('/')[0] !== sellerSlug
      );

      updatedMethods.push(newEntry);

      return {
        ...state,
        selectedSellersDeliveryMethod: updatedMethods,
      };
    }

    case 'UPDATE_CART_CONTEXT':
      return generateCartState({ ...state, cartContext: action.payload }, state.items);

    default:
      throw new Error('No action specified')
  }
}

export const CartProvider: FC<{
  children?: ReactNode
  id?: string
  defaultItems?: Item[]
  onSetItems?: (items: Item[]) => void
  onItemAdd?: (payload: Item) => void
  onItemUpdate?: (payload: object) => void
  onItemRemove?: (id: Item['id']) => void
  onItemExpRemove?: (id: number) => void
  storage?: (key: string, initialValue: string) => [string, (value: Function | string) => void]
  metadata?: Metadata
  customer?: Customer
  coupon?: Coupon
  shipping?: Shipping
  block?: boolean | string
  zipCode?: string
  shippingType?: ShippingType
  note?: string
  onSetCustomer?: (customer: Customer) => void
  onSetCoupon?: (coupon: Coupon) => void
  onSetShipping?: (shipping: Shipping) => void
  onSetBlock?: (block: boolean | string) => void
  onSetZipCode?: (zipcode: string) => void
  onSetShippingType?: (shippingType: ShippingType) => void
  onSetNote?: (note: string) => void
  onSetPopUp?: (popup: boolean) => void
  popup?: boolean
}> = ({
  children,
  id: cartId,
  defaultItems = [],
  onSetItems,
  onItemAdd,
  onItemUpdate,
  onItemRemove,
  onItemExpRemove,
  storage = useLocalStorage,
  metadata,
  onSetCustomer,
  onSetCoupon,
  onSetShipping,
  onSetBlock,
  onSetZipCode,
  onSetShippingType,
  onSetNote,
  onSetPopUp,
  customer,
  coupon,
  shipping,
  block,
  shippingType,
  note,
  popup,
  zipCode,
}) => {
  const id = cartId || createCartIdentifier()
  const router = useRouter();

  const [savedCart, saveCart] = storage(
    cartId ? `cart-${id}` : 'cart',
    JSON.stringify({
      id,
      ...initialState,
      items: defaultItems,
      metadata,
      customer,
      coupon,
      shipping,
      block,
      shippingType,
      note,
      popup,
      zipCode,
    }),
  )

  const [state, dispatch] = useReducer(reducer, JSON.parse(savedCart))

  useEffect(() => {
    state.router = router;
    saveCart(JSON.stringify(state))
  }, [state, saveCart])

  useEffect(() => {
    //@ts-ignore
    dispatch({
      type: 'UPDATE_ITEM'
    })
  }, [router.query])

  const setItems = (items: Item[]) => {
    dispatch({
      type: 'SET_ITEMS',
      payload: items,
    })

    if (typeof onSetItems === 'function') {
      onSetItems(items)
    }
  }

  const addItem = (item: Item, quantity = 1) => {
    if (!item.id) {
      throw new Error('You must provide an `id` for items')
    }

    if (quantity <= 0) {
      return
    }

    if (item.type === 'convencional') {
      const currentItem = state.items.find((i: Item) => i.id === item.id)

      // eslint-disable-next-line no-prototype-builtins
      if (!currentItem && !item.hasOwnProperty('price')) {
        throw new Error('You must pass a `price` for new items')
      }

      if (!currentItem) {
        const payload = { ...item, quantity }

        dispatch({ type: 'ADD_ITEM', payload })

        if (typeof onItemAdd === 'function') {
          onItemAdd(payload)
        }

        return
      }

      const payload = { ...item, quantity: currentItem.quantity + quantity }

      dispatch({
        type: 'UPDATE_ITEM',
        id: item.id,
        payload,
      })
    }

    const currentItem = state.items.find((i: Item) => i.experiencia_id === item.experiencia_id)

    if (!currentItem) {
      const payload = { ...item, quantity }

      dispatch({ type: 'ADD_ITEM', payload })

      if (typeof onItemAdd === 'function') {
        onItemAdd(payload)
      }

      return
    }

    const payload = { ...item, quantity: currentItem.quantity + quantity }

    dispatch({
      type: 'UPDATE_ITEM',
      id: item.experiencia_id,
      payload,
    })

    // if (typeof onItemUpdate === 'function') {
    //   onItemUpdate(payload)
    // }
  }

  const updateItem = (itemID: Item['id'], payload: object) => {
    if (!itemID || !payload) {
      return
    }

    dispatch({ type: 'UPDATE_ITEM', id: itemID, payload })

    if (typeof onItemUpdate === 'function') {
      onItemUpdate(payload)
    }
  }

  const setPopup = (value: boolean = false) => {
    dispatch({ type: 'SET_POPUP', payload: value })

    if (typeof onSetPopUp === 'function') {
      onSetPopUp(value)
    }
  }

  const updatePeriod = (itemID: Item['id'], period: string) => {
    const currentItemConvencional = state.items.find(
      (item: Item) => item.id.toString() === itemID.toString() && item.type === 'convencional',
    )

    if (currentItemConvencional) {
      if (!currentItemConvencional) {
        throw new Error('No such item to update')
      }
      const payload = { ...currentItemConvencional, period }
      dispatch({
        type: 'UPDATE_ITEM',
        id: itemID,
        payload,
      })
      return
    }

  }

  const updateItemQuantity = (itemID: Item['id'], quantity: number) => {
    if (quantity <= 0) {
      if (typeof onItemRemove === 'function') {
        onItemRemove(itemID)
      }

      dispatch({ type: 'REMOVE_ITEM', id: itemID })

      return
    }

    const currentItemConvencional = state.items.find(
      (item: Item) => item.id.toString() === itemID.toString() && item.type === 'convencional',
    )

    if (currentItemConvencional) {
      if (!currentItemConvencional) {
        throw new Error('No such item to update')
      }
      const payload = { ...currentItemConvencional, quantity }
      dispatch({
        type: 'UPDATE_ITEM',
        id: itemID,
        payload,
      })
      return
    }

    const currentItemExp = state.items.find(
      (item: Item) => item.type === 'experiencia' && item.experiencia_id.toString() === itemID.toString(),
    )

    if (!currentItemExp) {
      throw new Error('No such item to update')
    }

    const payload = { ...currentItemExp, quantity }

    dispatch({
      type: 'UPDATE_ITEM',
      id: itemID,
      payload,
    })
  }

  const removeItem = (itemID: Item['id']) => {
    if (!itemID) {
      return
    }

    dispatch({ type: 'REMOVE_ITEM', id: itemID })

    if (typeof onItemRemove === 'function') {
      onItemRemove(itemID)
    }
  }

  const removeExpItem = (itemExpID: number) => {
    if (!itemExpID) {
      return
    }

    dispatch({ type: 'REMOVE_EXP_ITEM', id: itemExpID })

    if (typeof onItemExpRemove === 'function') {
      onItemExpRemove(itemExpID)
    }
  }

  const emptyCart = () =>
    dispatch({
      type: 'EMPTY_CART',
    })

  const getItem = (itemID: Item['id']) => {
    return state.items.find((i: Item) => i.id === itemID)
  }

  const inCart = (itemID: Item['id']) => {
    return state.items.some((i: Item) => i.id === itemID)
  }

  const setCustomer = (payload: Customer) => {
    if (!payload) {
      return
    }

    dispatch({ type: 'SET_CUSTOMER', payload })

    if (typeof onSetCustomer === 'function') {
      onSetCustomer(payload)
    }
  }

  const setCoupon = (payload: Coupon) => {
    if (!payload) {
      return
    }

    dispatch({ type: 'SET_COUPON', payload })

    if (typeof onSetCoupon === 'function') {
      onSetCoupon(payload)
    }
  }

  const setBlock = (payload: boolean | string) => {
    dispatch({ type: 'SET_BLOCK', payload })

    if (typeof onSetBlock === 'function') {
      onSetBlock(payload)
    }
  }

  const setShipping = (payload: Shipping) => {
    if (!payload) {
      return
    }

    dispatch({ type: 'SET_SHIPPING', payload })

    if (typeof onSetShipping === 'function') {
      onSetShipping(payload)
    }
  }

  const setZipCode = (payload: string) => {
    if (!payload) {
      return
    }

    setCookie(null, 'zipcode', payload, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/',
    })

    dispatch({ type: 'SET_ZIPCODE', payload })

    if (typeof onSetZipCode === 'function') {
      onSetZipCode(payload)
    }
  }

  const updateCartMetadata = (payload: Metadata) => {
    if (!payload) {
      return
    }

    dispatch({
      type: 'UPDATE_CART_META',
      payload,
    })
  }

  const setShippingType = (payload: ShippingType) => {
    if (!payload) {
      return
    }

    setCookie(null, 'shipping-type', payload, {
      maxAge: 30 * 24 * 60 * 60,
      path: '/',
    })

    dispatch({
      type: 'SET_SHIPPING_TYPE',
      payload,
    })

    if (typeof onSetShippingType === 'function') {
      onSetShippingType(payload)
    }
  }

  const setNote = (payload: string) => {
    if (!payload) {
      return
    }

    dispatch({ type: 'SET_NOTE', payload })

    if (typeof onSetNote === 'function') {
      onSetNote(payload)
    }
  }

  const setDeliveryMethod = (itemID: Item['id'], delivery_method: string) => {
    const currentItem = state.items.find(
      (item: Item) => item.id.toString() === itemID.toString()
    )

    if(!currentItem) return;

    const updatedItem = {
      ...currentItem,
      delivery_method: delivery_method,
    }

    dispatch({
      type: 'UPDATE_ITEM',
      id: itemID,
      payload: updatedItem,
    })
  };

  const setSelectedSellersDeliveryMethod = (value: string[]) => {
    dispatch({ type: 'SET_SELECTED_SELLERS_DELIVERY_METHOD', payload: value });
  };

  const updateSellerMethodDeliveryKey = (newEntry: string) => {
    const sellerSlug = newEntry.split('/')[0];

    dispatch({
      type: 'UPDATE_SELECTED_SELLERS_DELIVERY_METHOD',
      payload: newEntry,
    });
  };

  const updateCartContext = useCallback((
    context: 'conventional' | 'subscription' | 'experience' | null
  ) => {
    if(context === 'experience') state.shipping = [];
    dispatch({ type: 'UPDATE_CART_CONTEXT', payload: context });
  }, []);

  useEffect(() => {
    if (!state.zipCode) {
      const cookies = parseCookies()
      const storedCookie = (cookies.zipcode as never) || ''
      if (storedCookie) {
        setZipCode(storedCookie)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const autoUpdateCartContext = useCallback(() => {
    const { exp, subscription } = router.query;
    if (exp === 'true') {
      dispatch({ type: 'UPDATE_CART_CONTEXT', payload: 'experience' });
    } else if (subscription === 'true') {
      dispatch({ type: 'UPDATE_CART_CONTEXT', payload: 'subscription' });
    } else if (exp === 'false' && subscription === 'false') {
      dispatch({ type: 'UPDATE_CART_CONTEXT', payload: 'conventional' });
    } else {
      dispatch({ type: 'UPDATE_CART_CONTEXT', payload: null });
    }
  }, [router.query]);

  useEffect(() => {
    autoUpdateCartContext();
  }, [router.query, autoUpdateCartContext]);

  return (
    <CartContext.Provider
      value={{
        ...state,
        totalShipping: calculateTotalShipping(state.shipping, state.selectedSellersDeliveryMethod),
        getItem,
        inCart,
        setItems,
        addItem,
        updateItem,
        updateItemQuantity,
        updatePeriod,
        removeItem,
        removeExpItem,
        emptyCart,
        updateCartMetadata,
        setCustomer,
        setCoupon,
        setShipping,
        setBlock,
        setZipCode,
        setShippingType,
        setNote,
        setPopup,
        setDeliveryMethod,
        setSelectedSellersDeliveryMethod,
        updateSellerMethodDeliveryKey,
        itemsToOrder: state.itemsToOrder,
        updateCartContext,
        autoUpdateCartContext
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
